import { useState, useEffect, useRef } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { categories } from "../data/mainData";
import About from "./About";
import Contact from "./Contact";

// ─── Demo Data ───────────────────────────────────────────────────────────────

const slides = [
  {
    id: 1,
    title: "From Prairie Fields to Your Dinner Plates — ",
    sub: "Delivering Nature’s Finest Grains with Excellence.",
    img:"/assets/slider/sunrise.png",
    // img: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1600&q=80",
    accent: "#c8a45a",
  },
  {
    id: 2,
    title: "Rooted in Tradition, Growing with Global Excellence",
sub: "Inspired by centuries of agricultural heritage, we deliver premium Canadian pulses and grains to international markets with unmatched quality, consistency, and trust.",

    img:"/assets/slider/lentils.jpg",
    // img: "https://images.unsplash.com/photo-1464226184884-fa280b87c399?w=1600&q=80",
    accent: "#7a9e6e",
  },
  {
    id: 3,
    title: "Committed to sourcing the true jewels of the Canadian Prairies —",
    sub: "Premium grains rich in nutrition, delivering quality, nutrition, and trust through an efficient supply chain",
    // sub: "delivering quality, nutrition, and trust through an efficient supply chain.",
    img:"/assets/slider/lentils-2.jpg",
    // img: "https://images.unsplash.com/photo-1574323347407-f5e1ad6d020b?w=1600&q=80",
    accent: "#c8a45a",
  },
];



const countries = [
  { name: "India", img: "/assets/countries/india.png", flag: "🇮🇳", product: "Red Lentils, Chickpeas" },
  { name: "United Kingdom", img: "/assets/countries/uk.png", flag: "🇬🇧", product: "Green Lentils, Yellow Peas" },
  { name: "Germany", img: "/assets/countries/germany.png", flag: "🇩🇪", product: "Organic Pulses" },
  { name: "UAE", img: "/assets/countries/uae.png", flag: "🇦🇪", product: "Mixed Lentils" },
  { name: "South Korea", img: "/assets/countries/south-korea.png", flag: "🇰🇷", product: "Canary Seeds, Peas" },
  { name: "Brazil", img: "/assets/countries/brazil.png", flag: "🇧🇷", product: "Black Beans, Lentils" },
  { name: "South Africa", img: "/assets/countries/south-africa.jpg", flag: "🇿🇦", product: "Chickpeas, Red Lentils" },
  { name: "Kenya", img: "/assets/countries/kenya.png", flag: "🇰🇪", product: "Yellow Peas, Lentils" },
];

const faqs = [
  {
    q: "What quality certifications does Celestial Grains hold?",
    a: "We maintain HACCP, ISO 22000, Phytosanitary and Canadian Grain Commission certifications. Every shipment undergoes rigorous lab testing for moisture, purity, and contaminants before export.",
  },
  {
    q: "What is a phytosanitary certificate?",
    a: "A phytosanitary certificate is an official document issued to certify that agricultural products are free from pests and comply with the importing country’s plant health regulations.",
  },
  {
    q: "Do you provide phytosanitary certification support?",
    a: "Yes, we assist with phytosanitary certification and export compliance documentation for eligible agricultural shipments.",
  },
  {
    q: "What is your minimum order quantity (MOQ)?",
    a: "Our standard MOQ is 20 metric tonnes per product, roughly one full container load (FCL). We can discuss smaller trial orders for new partners.",
  },
  {
    q: "How long does shipment take?",
    a: "Transit times vary by destination: Travel time from Calgary to India 35-50 days, South Korea 25-30 days, Europe 20–30 days, UAE 40-45 days. We provide real-time tracking for every container.",
  },
  {
    q: "Do you offer custom packaging and private labelling?",
    a: "Yes. We offer 25 kg PP bags, bulk containers, and custom branded packaging. Our team handles all labelling compliance for your target country.",
  },
  {
    q: "How does Celestial Grains ensure traceability?",
    a: "Every batch is traceable back to the farm of origin. We maintain detailed farm records, lot numbers, and third-party lab reports — available to buyers on request.",
  },
];

const certificates = [
  {
    id: 1,
    name: "Phytosanitary Certificate",
    issuer: "Canadian Food Inspection Agency",
    desc: "Confirms grain shipments are free from pests and plant diseases per international trade standards.",
    logo: "/assets/certificates/phytosanitary.jpeg",
    logoBg: "#fff",
  },
  {
    id: 2,
    name: "Certificate of Origin",
    issuer: "Canadian Chamber of Commerce",
    desc: "Verifies that our grains are genuinely grown and processed in Canada for customs compliance.",
    logo: "/assets/certificates/origin-3.png",
    logoBg: "#fff",
  },
  {
    id: 3,
    name: "Radiation Certificate",
    issuer: "Health Canada",
    desc: "Confirms all products meet safe radiation exposure standards required for international export.",
    logo: "/assets/certificates/radiation-2.jpg",
    logoBg: "#fff",
  },
  {
    id: 4,
    name: "Preventive Control Plan",
    issuer: "Safe Food for Canadians Act",
    desc: "Comprehensive food safety program meeting all SFCR requirements for Canadian export licensing.",
    logo: "/assets/certificates/cfia.jpeg",

    logoBg: "#fff",
  },
  {
    id: 5,
    name: "HACCP Certification",
    issuer: "Hazard Analysis & Critical Control Points",
    desc: "Internationally recognized food safety management system for systematic hazard prevention.",
    logo: "/assets/certificates/haccp.jpeg",
    logoBg: "#fff",
  },
  {
    id: 6,
    name: "Organic Certification",
    issuer: "Canada Organic Regime (COR)",
    desc: "Certified under Canada's rigorous organic standards — no synthetic pesticides or fertilizers.",
    logo: "/assets/certificates/organic.jpeg",
    logoBg: "#fff",
  },
  {
    id: 7,
    name: "Non-GMO / Glyphosate-Free",
    issuer: "Non-GMO Project Verified",
    desc: "Third-party verified free from genetically modified organisms and glyphosate residue.",
    logo: "/assets/certificates/non-gmo.png",
    logoBg: "#fff",
  },
];

// ─── Hero Slider ─────────────────────────────────────────────────────────────

function HeroSlider() {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    const t = setInterval(() => goTo((current + 1) % slides.length), 5500);
    return () => clearInterval(t);
  }, [current]);

  function goTo(idx) {
    if (animating || idx === current) return;
    setAnimating(true);
    setTimeout(() => {
      setCurrent(idx);
      setAnimating(false);
    }, 400);
  }

  const s = slides[current];

  return (
    <section className="hero" id="home">
      <div
        className={`hero__bg ${animating ? "fade-out" : "fade-in"}`}
        style={{ backgroundImage: `url(${s.img})` }}
      />
      <div className="hero__overlay" />
      <div className="hero__grain" />
      <div className="hero__content">
        <span className="hero__eyebrow">Alberta · Canada</span>
        <h3 className={`hero__title ${animating ? "slide-out" : "slide-in"}`}>
          {s.title}
        </h3>
        <p className={`hero__sub ${animating ? "slide-out" : "slide-in"}`}>
          {s.sub}
        </p>
        <div className="hero__ctas">
          <a href="#products" className="btn btn--gold">
            Explore Products
          </a>
          <a href="#contact" className="btn btn--outline">
            Get a Quote
          </a>
        </div>
      </div>
      <div className="hero__dots">
        {slides.map((_, i) => (
          <button
            key={i}
            className={`dot ${i === current ? "dot--active" : ""}`}
            onClick={() => goTo(i)}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>
      <div className="hero__scroll">
        <span />
      </div>
    </section>
  );
}

// ─── Products ────────────────────────────────────────────────────────────────

function Products() {
  const [active, setActive] = useState(null);
  
const navigate = useNavigate();


  return (
    <section className="section products" id="products">
      <div className="container">
        <div className="section__header">
          <span className="section__label">Our Portfolio</span>
          <h2 className="section__title">Product Categories</h2>
          <p className="section__desc">
            Every grain sourced from Alberta's premier farms — tested, graded,
            and export-ready.
          </p>
        </div>
        <div className="products__grid">
          {categories.map((c) => (
            <div
              key={c.id}
              className={`product-card ${active === c.id ? "product-card--open" : ""}`}
              onClick={() => {
                setActive(active === c.id ? null : c.id)
                navigate("/product/" + c.slug)
              }
              }
            >
              <div className="product-card__img-wrap">
                <img src={c.img} alt={c.name} className="product-card__img" />
                <span className="product-card__tag">{c.tag}</span>
              </div>
              <div className="product-card__body">
                <h3 className="product-card__name">{c.name}</h3>
                <p className="product-card__desc">{c.desc}</p>
                <button className="product-card__cta">
                  Read more <span>→</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Exports ─────────────────────────────────────────────────────────────────

function Certifications() {
  return (
   <section className="section certifications" id="certifications">
    
  <div className="container certifications__inner">

<div className="section__header">
          <span className="section__label">Global Compliance</span>
          <h2 className="section__title">Export Certifications & Quality Assurance</h2>
          <p className="section__desc">
        At Celestial Grains, we maintain strict international export standards to ensure smooth global trade and reliable product quality across every shipment.
          </p>
        </div>
    <div className="certifications__content">
     
      <div className="certifications__grid">
        <div className="cert-card">
          <h3>Phytosanitary Certification</h3>
          <p>
            We support phytosanitary certification processes to ensure
            agricultural products comply with importing country plant
            health and pest-control regulations.
          </p>
        </div>

        <div className="cert-card">
          <h3>Quality Inspection</h3>
          <p>
            Every shipment undergoes rigorous grading and quality
            verification before export dispatch.
          </p>
        </div>

        <div className="cert-card">
          <h3>Traceable Supply Chain</h3>
          <p>
            From farm sourcing to container loading, our operations
            prioritize transparency and full traceability.
          </p>
        </div>

        <div className="cert-card">
          <h3>Global Export Compliance</h3>
          <p>
            We coordinate documentation, customs procedures, and export
            standards for efficient international delivery.
          </p>
        </div>
      </div>
    </div>
  </div>
</section>
  );
}

// ─── Exports ─────────────────────────────────────────────────────────────────

function Exports() {
  return (
    <section className="section exports" id="exports">
      <div className="container">
        <div className="section__header section__header--light">
          <span className="section__label section__label--light">
            Global Reach
          </span>
          <h2 className="section__title section__title--light">
            Exported to Countries
          </h2>
          <p className="section__desc section__desc--light">
            From the Canadian prairies to plates around the world.
          </p>
        </div>
        <div className="exports__grid">
          {countries.map((c) => (
            <div key={c.name} className="export-card">
              {/* <span className="export-card__flag">{c.flag}</span> */}
              <img src={c.img} style={{width:"5vh"}} className="export-card__flag"/>
              <div>
                <strong className="export-card__name">{c.name}</strong>
                <span className="export-card__product">{c.product}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="exports__stat-row">
          {[
            { num: "20+", label: "Countries" },
            { num: "500K+", label: "Tonnes Exported" },
            { num: "200+", label: "Partner Farms" },
            { num: "99.8%", label: "On-time Delivery" },
          ].map((s) => (
            <div key={s.label} className="stat">
              <span className="stat__num">{s.num}</span>
              <span className="stat__label">{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── CertificatesSlider ─────────────────────────────────────────────────────────────────
    // Fallback: render a styled text badge if logo fails to load
const LogoBadge = ({ src, alt, bg }) => {
  const [err, setErr] = useState(false);
  if (err) {
    return (
      <div style={{
        width: "100%", height: "100%",
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "rgba(201,164,74,0.08)",
        borderRadius: "10px",
        padding: "6px",
      }}>
        <span style={{
          fontSize: "10px", fontWeight: 600, letterSpacing: "0.08em",
          textTransform: "uppercase", color: "#c9a44a", textAlign: "center",
          lineHeight: 1.3, fontFamily: "'Jost', sans-serif",
        }}>{alt}</span>
      </div>
    );
  }
}

function CertificatesSlider() {
   const [active, setActive] = useState(0);
  const autoRef = useRef(null);
  const total = certificates.length;
 
  const go = (idx) => setActive((idx + total) % total);
 
  const resetAuto = () => {
    clearInterval(autoRef.current);
    autoRef.current = setInterval(() => setActive((i) => (i + 1) % total), 4000);
  };
 
  useEffect(() => {
    autoRef.current = setInterval(() => setActive((i) => (i + 1) % total), 4000);
    return () => clearInterval(autoRef.current);
  }, []);
 
  const handleNav = (dir) => { go(active + dir); resetAuto(); };
 
  // Show 4 cards, translate so active is always 2nd from left (index 1)
  const SHOW = 4;
  const offset = active; // how many to shift left



  return (
 
        <section className="certs-section" id="certifications">
        <div className="certs-container">
 
          <div className="certs-header">
            <span className="certs-label">Compliance &amp; Trust</span>
            <h2 className="certs-title">
              Licences &amp; <em>Certifications</em>
            </h2>
            <p className="certs-subdesc">
              Every shipment backed by internationally recognized standards.
            </p>
          </div>
 
          <div className="certs-slider-wrap">
            <div className="certs-overflow">
              <div
                className="certs-track"
                style={{
                  transform: `translateX(calc(-${offset} * (220px + 14px)))`,
                }}
              >
                {/* Duplicate array for seamless looping feel */}
                {[...certificates, ...certificates].map((cert, idx) => (
                  <div
                    key={`${cert.id}-${idx}`}
                    className={`certs-card${(idx % total) === active ? " certs-card--active" : ""}`}
                    onClick={() => { go(idx % total); resetAuto(); }}
                  >
                    <div className="certs-card__logo-wrap">
                      <img
                        src={cert.logo}
                        alt={cert.issuer} style={{ width: "72px",
          height: "100px",
          objectFit:"contain",objectPosition:"center center"}}/>
                      {/* <LogoBadge src={cert.logo} alt={cert.issuer} bg={cert.logoBg} /> */}
                    </div>
                    <span className="certs-card__num">
                      {String(cert.id).padStart(2, "0")} / {String(total).padStart(2, "0")}
                    </span>
                    <strong className="certs-card__name">{cert.name}</strong>
                    <span className="certs-card__issuer">{cert.issuer}</span>
                    <div className="certs-card__rule" />
                    <p className="certs-card__desc">{cert.desc}</p>
                    <div className="certs-card__arrow">
                      <svg viewBox="0 0 16 16" fill="none">
                        <path d="M4 8h8M9 5l3 3-3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                ))}
              </div>
            </div>
 
            <div className="certs-progress">
              <div
                className="certs-progress-fill"
                style={{ width: `${((active + 1) / total) * 100}%` }}
              />
            </div>
          </div>
 
          <div className="certs-controls">
            <button className="certs-btn" onClick={() => handleNav(-1)} aria-label="Previous">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="certs-dots">
              {certificates.map((_, i) => (
                <button
                  key={i}
                  className={`certs-dot${i === active ? " certs-dot--active" : ""}`}
                  onClick={() => { go(i); resetAuto(); }}
                  aria-label={`Go to ${i + 1}`}
                />
              ))}
            </div>
            <button className="certs-btn" onClick={() => handleNav(1)} aria-label="Next">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
 
        </div>
      </section>
  );
}

// ─── FAQ ─────────────────────────────────────────────────────────────────────

function FAQ() {
  const [open, setOpen] = useState(null);

  return (
    <section className="section faq" id="faq">
      <div className="container faq__inner">
        <div className="faq__left">
          <span className="section__label">Got Questions?</span>
          <h2 className="section__title">Frequently Asked</h2>
          <p>
            Can't find your answer? Reach out to our export team — we respond
            within one business day.
          </p>
          <a href="#contact" className="btn btn--gold" style={{ marginTop: "1.5rem" }}>
            Contact Us
          </a>
        </div>
        <div className="faq__list">
          {faqs.map((f, i) => (
            <div
              key={i}
              className={`faq-item ${open === i ? "faq-item--open" : ""}`}
            >
              <button
                className="faq-item__q"
                onClick={() => setOpen(open === i ? null : i)}
              >
                <span>{f.q}</span>
                <span className="faq-item__icon">{open === i ? "−" : "+"}</span>
              </button>
              <div className="faq-item__a">
                <p>{f.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


export default function Home() {
   const location = useLocation();

  useEffect(() => {
    // Handle hash scrolling
    if (location.hash) {
      const element = document.querySelector(location.hash);

      if (element) {
        setTimeout(() => {
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 100);
      }
    } else {
      // Scroll to top when opening homepage normally
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [location]);

  return (
    <div className="app">
      <HeroSlider />
      <Products />
      <About/>
      <Exports />
      <Certifications/>
      <CertificatesSlider />
      <FAQ />
      <Contact />
    </div>
  );
}